/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Package, Truck } from "@phosphor-icons/react";
import { formatDeliveryEstimate } from "utils/deliveryEstimate";
import CepModal from "./CepModal";
import { fetchShippingQuote } from "./fetchShippingQuote";
import { getWhitelabelFromMemory } from "../../lib/sharedSessionMemory";
import {
  ShippingSection,
  ShippingTitle,
  ShippingBox,
  ShippingBoxError,
  ShippingInfoRow,
  ShippingInfoText,
  ShippingText,
  ShippingAddress,
  ChangeAddressLink,
  SkeletonLine,
  PrimaryButton,
} from "./styles";

function formatAddress(userAddress) {
  if (!userAddress) return "";
  const { streetName, number, neighborhood, city, state } = userAddress;
  const parts = [];
  if (streetName) parts.push(number ? `${streetName}, ${number}` : streetName);
  if (neighborhood) parts.push(neighborhood);
  if (city && state) parts.push(`${city}/${state}`);
  else if (city) parts.push(city);
  else if (state) parts.push(state);
  return parts.join(" - ");
}

function ShippingEstimate({ sku, userCep, userAddress, userUuid, onCartAdded, onAddressChange, onError, onShippingCalculated, onShippingStateChange, displayMode }) {
  // Start in "loading" so the skeleton is visible while the parent
  // resolves `userCep` asynchronously (addressesData fetch in UserContext).
  const [shippingState, setShippingState] = useState("loading");
  const [shippingData, setShippingData] = useState({});
  const [cep, setCep] = useState("");
  const [showCepModal, setShowCepModal] = useState(false);
  const lastQuotedCep = useRef(null);

  const calculateShipping = useCallback(
    async (zipCode) => {
      setShippingState("loading");
      setCep(zipCode);

      try {
        const channel = process.env.REACT_APP_DEFAULT_CHANNEL;
        const configData = getWhitelabelFromMemory()?.config || {};
        const fatorFrete = configData[`fator_${channel}_frete`] / 10000 || 0;

        const { deliveryOptions, prevQuantity } = await fetchShippingQuote({
          sku,
          cep: zipCode,
          customerUuid: userUuid,
          fatorFrete,
        });

        if (
          !deliveryOptions ||
          deliveryOptions.shippingPrice === null ||
          deliveryOptions.shippingPrice === undefined
        ) {
          setShippingState("no-delivery");
          setShippingData({ address: formatAddress(userAddress) });
          onCartAdded?.(prevQuantity > 0);
          onError?.(false);
          lastQuotedCep.current = zipCode;
          return;
        }

        setShippingData({
          price: deliveryOptions.shippingPrice,
          points: deliveryOptions.shippingPoints,
          estimatedDays: deliveryOptions.estimatedBusinessDays,
          estimatedDate: deliveryOptions.estimatedDeliveryDate,
          address: formatAddress(userAddress),
        });
        setShippingState("calculated");
        onShippingCalculated?.();
        onCartAdded?.(prevQuantity > 0);
        onError?.(false);
        lastQuotedCep.current = zipCode;

      } catch (error) {
        console.error("Erro ao calcular frete:", error);
        setShippingState("error");
        onCartAdded?.(false);
        onError?.(true);
      }
    },
    [sku, userUuid, userAddress, onCartAdded]
  );

  useEffect(() => {
    onShippingStateChange?.(shippingState);
  }, [shippingState, onShippingStateChange]);

  // Drive the state from the effective CEP (modal-entered `cep` wins over
  // the parent-supplied `userCep`). Re-runs when sku, userCep or userUuid
  // change — so if addresses load async (userCep arrives later), the
  // calculation kicks in automatically. Falls to "idle" only once we
  // know the user is loaded but has no CEP; otherwise keeps the skeleton.
  useEffect(() => {
    const effectiveCep = cep || userCep;
    if (!effectiveCep) {
      if (userUuid) setShippingState("idle");
      return;
    }
    if (lastQuotedCep.current === effectiveCep) return;
    calculateShipping(effectiveCep);
    // eslint-disable-next-line
  }, [sku, userCep, userUuid]);

  const renderContent = () => {
    switch (shippingState) {
      case "idle":
        return (
          <ShippingBox
            onClick={() => setShowCepModal(true)}
            style={{ cursor: "pointer" }}
          >
            <Package size={20} color="#00003C" weight="regular" />
            <ShippingText>Informe um CEP</ShippingText>
          </ShippingBox>
        );

      case "loading":
        return (
          <ShippingBox>
            <SkeletonLine w="70%" h="20px" />
            <SkeletonLine w="40%" h="14px" />
          </ShippingBox>
        );

      case "calculated": {
        const formattedDate = formatDeliveryEstimate({
          estimatedDeliveryDate: shippingData.estimatedDate,
          estimatedBusinessDays: shippingData.estimatedDays,
        });

        const formatShippingValue = () => {
          const money = shippingData.price || 0;
          const points = shippingData.points || 0;
          const moneyFormatted = `R$ ${money.toFixed(2).replace(".", ",")}`;
          const pointsFormatted = `${Math.round(points).toLocaleString("pt-BR")} pts`;

          if (displayMode === "points") return pointsFormatted;
          if (displayMode === "both") return `${moneyFormatted} / ${pointsFormatted}`;
          return moneyFormatted;
        };

        return (
          <>
            <ShippingBox>
              <ShippingInfoRow>
                <Truck size={20} color="#00003C" weight="regular" />
                <ShippingInfoText>
                  Chega até dia {formattedDate} • {formatShippingValue()}
                </ShippingInfoText>
              </ShippingInfoRow>
              <ShippingAddress>{shippingData.address}</ShippingAddress>
            </ShippingBox>
            <ChangeAddressLink onClick={() => setShowCepModal(true)}>
              Alterar endereço
            </ChangeAddressLink>
          </>
        );
      }

      case "no-delivery":
        return (
          <>
            <ShippingBoxError>
              <ShippingInfoRow>
                <Truck size={24} color="#00003C" weight="regular" />
                <ShippingInfoText>
                  Não realizamos entrega no CEP informado.
                </ShippingInfoText>
              </ShippingInfoRow>
              <ShippingAddress>{shippingData.address}</ShippingAddress>
            </ShippingBoxError>
            <ChangeAddressLink onClick={() => setShowCepModal(true)}>
              Informar outro CEP de entrega
            </ChangeAddressLink>
          </>
        );

      case "error":
        return (
          <>
            <ShippingBox>
              <ShippingText>
                Ops! Não foi possível carregar seu CEP.
              </ShippingText>
            </ShippingBox>
            <PrimaryButton onClick={() => setShowCepModal(true)}>
              INFORMAR OUTRO CEP
            </PrimaryButton>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <ShippingSection>
      {renderContent()}
      <CepModal
        show={showCepModal}
        onClose={() => setShowCepModal(false)}
        onCepConfirm={(newCep, data) => {
          setCep(newCep);
          lastQuotedCep.current = newCep;
          setShippingData({
            price: data.price,
            points: data.points,
            estimatedDays: data.estimatedDays,
            estimatedDate: data.estimatedDate,
            address: data.address,
          });
          if (data.price !== undefined && data.price !== null) {
            setShippingState("calculated");
            onShippingCalculated?.();
          } else {
            setShippingState("no-delivery");
          }
          onCartAdded?.(Boolean(data.wasInCart));
          if (data.addressDetails) {
            onAddressChange?.(data.addressDetails);
          }
          setShowCepModal(false);
        }}
        sku={sku}
        userUuid={userUuid}
        displayMode={displayMode}
      />
    </ShippingSection>
  );
}

export default ShippingEstimate;
